import React, { useEffect, useState } from "react";
import { Layout, Button, Divider, List, Typography } from "antd";
import { useRouter } from "next/router";
import { SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import xor from "lodash/xor";
import Services from "../../services";
import Helpers from "../../services/helpers";
import Toaster from "../../services/toaster";
import SaveSearchForm from "../search/SaveSearchForm";
import { useCardsContext } from "../contexts/cardcontext";
import { useSearchContext } from "../contexts/searchcontext.ts";
import { useSearchOverlayContext } from "../contexts/searchOverlayContext";
import { isMobile } from "react-device-detect";
import useGlobal from "../../store";
import { generateCardTitle } from "utils/cardList";
import { usePopularChartContext } from "../contexts/popularchartcontext";
import { SellerBuyerOptions } from "../SellerBuyerOptions";
import { ENTITY_TYPES } from "utils/constants";
import { useWaxesContext } from "components/contexts/WaxContext";

// entityIds is a string like "1,2,3,4"
const MultipleEntitiesMenu = ({ entityIds, savedSearchType, collapseSider, entityType }) => {
  const searchContext = useSearchContext();
  const { cards, setCards, setRatios: setCardRatios } = useCardsContext();
  const { waxes, setWaxes, setRatios: setWaxRatios } = useWaxesContext();
  const { setShowOverlay } = useSearchOverlayContext();
  const popularChartContext = usePopularChartContext();
  const router = useRouter();
  const [globalState, globalActions] = useGlobal();
  const [showSavedSearchForm, setShowSavedSearchForm] = useState(false);
  const [initialExcludedSellers, setInitialExcludedSellers] = useState(
    globalState.excludeSellersFromChartResults || [],
  );
  const [initialExcludedPlatforms, setInitialExcludedPlatforms] = useState(
    globalState.excludePlatformsFromChartResults || [],
  );
  const [initialFeedbackExclusion, setInitialFeedbackExclusion] = useState({
    excludeNoFeedback: globalState.excludeNoFeedback,
    excludeNoSellerFeedback: globalState.excludeNoSellerFeedback,
    excludeLowFeedback: globalState.excludeLowFeedback,
  });
  const [initialAuctionExclusions, setInitialAuctionExclusions] = useState({
    excludeSaleWithMakeOffer: globalState.excludeSaleWithMakeOffer,
    excludeAuctions: globalState.excludeAuctions,
    excludeBuyItNow: globalState.excludeBuyItNow,
  });

  const [
    entities,
    shortIdentifier,
    resultIdentifier,
    individualStatsIdentifier,
    searchFn,
    setEntitiesFn,
    setRatiosFn,
    setChartEntitiesFn,
    setPopularStatsFn,
    generateTitleFn,
  ] = {
    [ENTITY_TYPES.CARD]: [
      cards,
      "cid",
      "cards",
      "individualCardStats",
      Services.searchCardResults,
      setCards,
      setCardRatios,
      popularChartContext.setCards,
      searchContext.setPopularCardStats,
      generateCardTitle,
    ],
    [ENTITY_TYPES.SEALED_WAX]: [
      waxes,
      "swid",
      "waxes",
      "individualSealedWaxStats",
      Services.searchSealedWaxResults,
      setWaxes,
      setWaxRatios,
      popularChartContext.setSealedWaxes,
      searchContext.setPopularSealedWaxStats,
      Helpers.getSealedWaxName,
    ],
  }[entityType];

  const search = async (resetDates = false) => {
    try {
      globalActions.setShowGlobalSpinner(true);

      if (isMobile) {
        collapseSider(true);
      }

      const dates = {
        startDate: searchContext.startDate.toDate(),
        endDate: searchContext.endDate.toDate(),
      };

      if (resetDates) {
        dates.startDate = moment().subtract(90, "d").startOf("d");
        dates.endDate = moment();
        searchContext.setStartDate(dates.startDate);
        searchContext.setEndDate(dates.endDate);
      }

      const {
        excludeSellersFromChartResults,
        excludePlatformsFromChartResults,
        excludeNoFeedback,
        excludeNoSellerFeedback,
        excludeLowFeedback,
        excludeSaleWithMakeOffer,
        excludeAuctions,
        excludeBuyItNow,
      } = globalState;

      const params = {
        excludeSellersFromChartResults,
        excludePlatformsFromChartResults,
        excludeNoFeedback,
        excludeNoSellerFeedback,
        excludeLowFeedback,
        excludeSaleWithMakeOffer,
        excludeAuctions,
        excludeBuyItNow,
      };

      const result = await searchFn({
        id: entityIds,
        returnRatios: true,
        ...dates,
        ...params,
      });
      popularChartContext.setPointsByDay(result.chartData);
      setChartEntitiesFn(result[resultIdentifier]);
      setPopularStatsFn(result[individualStatsIdentifier]);

      searchContext.setDateRangeSearched(Object.values(dates));

      if (!result?.[resultIdentifier]?.length) {
        Toaster.WarningNotification("Search returned no results", "Try changing your selections.");
      } else {
        setEntitiesFn(result?.[resultIdentifier] || []);
        setRatiosFn(result?.ratios || []);
        setShowSavedSearchForm(true);
      }

      searchContext.setInitialSearchPerformed(true);
      searchContext.setIsSearchRunning(false);
      globalActions.setShowGlobalSpinner(false);
    } catch (e) {
      return Helpers.handleApiError(e, router);
    }
  };

  useEffect(() => {
    if (entityIds?.length) {
      search();
    }
  }, [entityIds]);

  useEffect(() => {
    const {
      excludeSellersFromChartResults,
      excludePlatformsFromChartResults,
      excludeNoFeedback,
      excludeNoSellerFeedback,
      excludeLowFeedback,
      excludeSaleWithMakeOffer,
      excludeAuctions,
      excludeBuyItNow,
    } = globalState;
    // Check if all elements are the same in the 2 arrays
    if (xor(excludeSellersFromChartResults, initialExcludedSellers).length) {
      setInitialExcludedSellers(excludeSellersFromChartResults);
      search();
    } else if (xor(excludePlatformsFromChartResults, initialExcludedPlatforms).length) {
      setInitialExcludedPlatforms(excludePlatformsFromChartResults);
      search();
    } else if (
      initialFeedbackExclusion.excludeNoFeedback !== excludeNoFeedback ||
      initialFeedbackExclusion.excludeNoSellerFeedback !== excludeNoSellerFeedback ||
      initialFeedbackExclusion.excludeLowFeedback !== excludeLowFeedback
    ) {
      setInitialFeedbackExclusion({ excludeNoFeedback, excludeNoSellerFeedback, excludeLowFeedback });
      search();
    } else if (
      excludeSaleWithMakeOffer !== initialAuctionExclusions.excludeSaleWithMakeOffer ||
      excludeAuctions !== initialAuctionExclusions.excludeAuctions ||
      excludeBuyItNow !== initialAuctionExclusions.excludeBuyItNow
    ) {
      setInitialAuctionExclusions({
        excludeSaleWithMakeOffer,
        excludeAuctions,
        excludeBuyItNow,
      });
      search();
    }
  }, [
    globalState.excludeSellersFromChartResults,
    globalState.excludePlatformsFromChartResults,
    globalState.excludeNoFeedback,
    globalState.excludeNoSellerFeedback,
    globalState.excludeLowFeedback,
    globalState.excludeSaleWithMakeOffer,
    globalState.excludeAuctions,
    globalState.excludeBuyItNow,
  ]);

  useEffect(() => {
    if (searchContext.initialSearchPerformed) {
      search();
    }
  }, [searchContext.startDate?.unix(), searchContext.endDate?.unix()]);

  return (
    <Layout className="card-filter-wizard" style={{ minHeight: "100vh" }}>
      <div style={{ padding: 12 }}>
        {entities.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <Typography.Text style={{ fontWeight: "700", fontSize: "1.25em" }}>This search</Typography.Text>
            <List>
              {entities.map((e) => (
                <List.Item key={e.id} style={{ cursor: "default" }}>
                  {generateTitleFn(e)}
                </List.Item>
              ))}
            </List>
          </div>
        )}

        <Button
          icon={<SearchOutlined />}
          type="secondary"
          style={{ width: "100%", marginBottom: 8 }}
          onClick={() => setShowOverlay(true)}
        >
          New Quick Search
        </Button>

        <Button
          icon={<SearchOutlined />}
          type="primary"
          style={{ width: "100%" }}
          onClick={() => router.push({ pathname: router.pathname })}
        >
          New Advanced Search
        </Button>

        {showSavedSearchForm && (
          <>
            <Divider />
            <SaveSearchForm
              getTerms={() => ({ [shortIdentifier]: entityIds })}
              type={savedSearchType}
              setShowSavedSearchForm={setShowSavedSearchForm}
            />
          </>
        )}
      </div>
      <div className="seller-buyer-options">
        <SellerBuyerOptions />
      </div>
    </Layout>
  );
};

export default MultipleEntitiesMenu;
